import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { graphql, compose } from 'react-apollo';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';
import SweetAlert from 'react-bootstrap-sweetalert';
import messages from './messages';
import Table from '../../components/Table';
import layoutStyle from '../../styles/base/layout.scss';

class ViewNotifications extends React.Component {
  static propTypes = {
    notificationListQuery: PropTypes.shape({
      loading: PropTypes.bool,
      notificationList: PropTypes.array,
    }).isRequired,
    deleteNotification: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showConfirmModal: false,
      notificationToDelete: null,
    };
    this.path = '/admin/notifications/';
    this.handleDeleteNotification = this.handleDeleteNotification.bind(this);
    this.cancelDelete = this.cancelDelete.bind(this);
    this.confirmDelete = this.confirmDelete.bind(this);
  }

  handleDeleteNotification(id) {
    this.setState({
      showConfirmModal: true,
      notificationToDelete: id,
    });
  }

  cancelDelete() {
    this.setState({
      showConfirmModal: false,
      notificationToDelete: null,
    });
  }

  confirmDelete() {
    this.props.deleteNotification(this.state.notificationToDelete);
    this.setState({
      showConfirmModal: false,
      notificationToDelete: null,
    });
  }

  createColumns() {
    const columns = [
      {
        Header: 'Title',
        accessor: 'title',
        Cell: ({ original }) => <span>{original.title}</span>,
      },
      {
        Header: 'Message',
        accessor: 'message',
        width: 400,
        Cell: ({ original }) => <span>{original.message}</span>,
      },
      {
        Header: 'Priority',
        accessor: 'priority',
        width: 100,
        Cell: ({ original }) => <span>{original.priority}</span>,
        sortMethod: (priority1, priority2) => {
          const priorityValues = { high: 3, medium: 2, low: 1 };
          return (
            priorityValues[priority1.toLowerCase()] -
            priorityValues[priority2.toLowerCase()]
          );
        },
      },
      {
        Header: 'Start',
        accessor: 'datetime_from',
        Cell: ({ value }) => (
          <span>{moment(value).format('DD.MM.YYYY HH:mm')}</span>
        ),
        sortMethod: (datetime1, datetime2) =>
          moment(datetime1).utc() - moment(datetime2).utc(),
      },
      {
        Header: 'End',
        accessor: 'datetime_to',
        Cell: ({ value }) => (
          <span>{moment(value).format('DD.MM.YYYY HH:mm')}</span>
        ),
        sortMethod: (datetime1, datetime2) =>
          moment(datetime1).utc() - moment(datetime2).utc(),
      },
      { Header: 'Creator', accessor: 'creator.username' },
      {
        Header: <FormattedMessage {...messages.actions} />,
        width: 150,
        Cell: ({ original }) => (
          <Row>
            <Col xs={1}>
              <Link to={`${this.path}${original.id}/edit`}>
                <FaEdit />
              </Link>
            </Col>
            {!original.active && (
              <Col xs={1}>
                <button
                  type="button"
                  onClick={() => this.handleDeleteNotification(original.id)}
                  className="btn-small"
                >
                  <FaTrash />
                </button>
              </Col>
            )}
          </Row>
        ),
      },
    ].map(column => ({
      ...column,
      Filter: () => null,
    }));
    return columns;
  }

  render() {
    const columns = this.createColumns();
    const { showConfirmModal } = this.state;

    return (
      <div>
        <h3>
          <FormattedMessage {...messages.notificationsHeading} />
        </h3>
        <Row style={{ marginBottom: '10px' }}>
          <Col xs={12}>
            <Link to={`${this.path}new`} className="btn btn-primary">
              <FaPlus className="icon--prepended icon-button" />
              <FormattedMessage {...messages.addNotification} />
            </Link>
          </Col>
        </Row>
        <Row className={layoutStyle.noMargin}>
          <Col xs={12} className={layoutStyle.noPadding}>
            <Table
              keyField="name"
              data={this.props.notificationListQuery.notificationList}
              columns={columns}
            />
          </Col>
        </Row>

        {showConfirmModal ? (
          <SweetAlert
            warning
            showCancel
            confirmBtnText={this.props.intl.formatMessage(
              messages.deleteConfirm,
            )}
            confirmBtnBsStyle="danger"
            cancelBtnBsStyle="default"
            title={this.props.intl.formatMessage(messages.deleteTitle)}
            onConfirm={this.confirmDelete}
            onCancel={this.cancelDelete}
          >
            <FormattedMessage {...messages.deleteQuestion} />
          </SweetAlert>
        ) : null}
      </div>
    );
  }
}

export const notificationListQuery = gql`
  query notificationList {
    notificationList {
      id
      title
      message
      datetime_from
      datetime_to
      priority
      creator {
        id
        username
      }
      created_at
      updated_at
    }
  }
`;

const deleteNotificationMutation = gql`
  mutation deleteNotification($id: ID!) {
    deleteNotification(id: $id)
  }
`;

export default compose(
  injectIntl,
  graphql(notificationListQuery, {
    name: 'notificationListQuery',
    options: {
      fetchPolicy: 'network-only',
    },
  }),
  graphql(deleteNotificationMutation, {
    props: ({ mutate }) => ({
      deleteNotification: id =>
        mutate({
          variables: { id },
          refetchQueries: [{ query: notificationListQuery }],
        }),
    }),
  }),
)(ViewNotifications);
